import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/components/BlogTeaser";
import { useSEO } from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useSEO({
    title: "Dental Blog | Teeth Talk from Glebe Park Dental, Canberra",
    description: "Plain-English dental advice from the Glebe Park Dental team in Canberra City — whitening, implants, kids' first visits, wisdom teeth and more.",
    path: "/blog",
  });

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <main className="bg-cream">
      <Nav />
      <section className="bg-sage pb-20 pt-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-leafmute">From the blog</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Teeth talk, <span className="italic text-fern">minus the jargon.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl font-sans text-sm leading-relaxed text-fern sm:text-base">
              The questions people ask us in the chair, answered properly. Written
              by the Glebe Park Dental team for Canberra patients — no waffle, no
              scare tactics.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-5 sm:px-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-8 lg:px-16">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={0.08 * (i % 3)} y={24}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid="blog-article-card"
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/8 bg-white shadow-[0_2px_20px_-8px_rgba(18,26,21,0.1)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-16px_rgba(18,26,21,0.25)]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-forest/80 px-3 py-1 font-mono text-xs tracking-[0.25em] uppercase text-leaf backdrop-blur">
                    {p.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h2 className="font-serif text-xl leading-snug text-ink transition-colors duration-300 group-hover:text-leafdeep">
                    {p.title}
                  </h2>
                  <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-fern">{p.excerpt}</p>
                  <p className="mt-5 font-mono text-xs tracking-[0.2em] uppercase text-fern/70">
                    {formatDate(p.date)} · {p.read_time}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {loaded && !posts.length && (
          <p className="py-20 text-center font-sans text-sm text-fern" data-testid="blog-empty-state">
            Nothing here yet — we're writing. Check back soon.
          </p>
        )}
      </section>
      <Footer />
    </main>
  );
}
